import OmSpinner from "@/components/OmSpinner";
import { T } from "@/components/T";
import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchFestivalPage } from "@/lib/api";
import { normalizeSrc } from "@/lib/images";
import RichText from "@/components/RichText";
import Seo from "@/components/Seo";
import FestivalCountdown from "@/components/FestivalCountdown";
import ShareButton from "@/components/ShareButton";
import { articleLd, breadcrumbLd, graph } from "@/lib/jsonld";
import { Check, ChevronRight, ArrowRight, Sparkles } from "lucide-react";

// ── individual rich block renderers ─────────────────────────────────────────
function QuoteBlock({ s, pick, heading }) {
  const lines = (pick(s.lines_hi, s.lines) || s.lines || "").split("\n").filter((l) => l.trim());
  if (!lines.length && !(s.body || s.body_hi)) return null;
  return (
    <section className="border-t border-line pt-8" data-testid="festival-block-quote">
      {heading}
      <blockquote className="mt-5 border-l-2 border-saffron bg-[var(--surface)]/60 px-6 py-6 md:px-8">
        <div className="space-y-2 font-deva text-lg leading-[2] text-ink md:text-xl">
          {lines.map((l, i) => <p key={i} className={i % 2 === 1 ? "pl-4 text-ink-2" : ""}>{l}</p>)}
        </div>
        {(s.body || s.body_hi) && <div className="mt-4 border-t border-line pt-3 text-sm italic text-ink-3"><RichText value={pick(s.body_hi, s.body) || s.body} /></div>}
      </blockquote>
    </section>
  );
}

function ChecklistBlock({ s, pick, storeKey, heading }) {
  const items = (s.items || []).filter((it) => it && (it.en || it.hi));
  const [checked, setChecked] = useState(() => { try { return JSON.parse(localStorage.getItem(storeKey) || "{}"); } catch { return {}; } });
  if (!items.length) return null;
  const toggle = (i) => { const n = { ...checked, [i]: !checked[i] }; setChecked(n); try { localStorage.setItem(storeKey, JSON.stringify(n)); } catch (_) {} };
  return (
    <section className="border-t border-line pt-8" data-testid="festival-block-checklist">
      {heading}
      <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
        {items.map((m, i) => (
          <li key={i}>
            <button onClick={() => toggle(i)} data-testid={`festival-check-${i}`}
              className={`group flex w-full items-start gap-3 border p-3.5 text-left transition-all ${checked[i] ? "border-[var(--gold)] bg-[rgba(184,134,11,0.08)]" : "border-line bg-parchment/60 hover:border-ink-3"}`}>
              <span className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors ${checked[i] ? "border-[var(--gold)] bg-[var(--gold)] text-white" : "border-ink-3"}`}>
                {checked[i] && <Check className="h-3.5 w-3.5" />}
              </span>
              <span className={`flex-1 text-[15px] leading-snug ${checked[i] ? "text-ink line-through decoration-[var(--gold)]/60" : "text-ink-2"}`}>{pick(m.hi, m.en) || m.en}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function FaqBlock({ s, pick, heading }) {
  const faqs = (s.faqs || []).filter((f) => f && (f.q || f.q_hi));
  const [open, setOpen] = useState(null);
  if (!faqs.length) return null;
  return (
    <section className="border-t border-line pt-8" data-testid="festival-block-faq">
      {heading}
      <div className="mt-5 divide-y divide-line border-y border-line">
        {faqs.map((f, i) => (
          <button key={i} data-testid={`festival-faq-${i}`} onClick={() => setOpen(open === i ? null : i)} className="flex w-full items-start justify-between gap-6 py-5 text-left transition-colors hover:bg-parchment/50">
            <div className="flex-1">
              <p className="text-lg text-ink font-deva-body">{pick(f.q_hi, f.q) || f.q}</p>
              {open === i && <div className="mt-3 max-w-3xl leading-relaxed text-ink-2"><RichText value={pick(f.a_hi, f.a) || f.a} /></div>}
            </div>
            <ChevronRight className={`mt-1 h-5 w-5 shrink-0 text-ink-3 transition-transform ${open === i ? "rotate-90" : ""}`} />
          </button>
        ))}
      </div>
    </section>
  );
}

function CardsBlock({ s, pick, heading }) {
  const cards = (s.cards || []).filter((c) => c && (c.title || c.title_hi));
  if (!cards.length) return null;
  const Card = ({ c }) => {
    const inner = (
      <>
        {(c.label || c.label_hi) && <span className="overline-label text-gold">{pick(c.label_hi, c.label)}</span>}
        <h3 className="mt-3 text-xl text-ink deva-lh font-deva">{pick(c.title_hi, c.title) || c.title}</h3>
        {(c.desc || c.desc_hi) && <p className="mt-2 flex-1 text-sm leading-relaxed text-ink-2 line-clamp-4">{pick(c.desc_hi, c.desc)}</p>}
        {c.href && <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-saffron transition-transform group-hover:translate-x-0.5">{pick("पढ़ें", "Read more")} <ArrowRight className="h-4 w-4" /></span>}
      </>
    );
    const cls = "group flex flex-col border border-line bg-parchment/60 p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[var(--gold)] hover:shadow-[0_8px_30px_rgba(44,38,33,0.08)]";
    if (!c.href) return <div className={cls}>{inner}</div>;
    return c.href.startsWith("/") ? <Link to={c.href} className={cls}>{inner}</Link> : <a href={c.href} target="_blank" rel="noreferrer" className={cls}>{inner}</a>;
  };
  return (
    <section className="border-t border-line pt-8" data-testid="festival-block-cards">
      {heading}
      <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {cards.map((c, i) => <Card key={i} c={c} />)}
      </div>
    </section>
  );
}

function HighlightBlock({ s, pick, heading }) {
  if (!(s.body || s.body_hi) && !(s.title || s.title_hi)) return null;
  return (
    <section data-testid="festival-block-highlight">
      <div className="relative overflow-hidden border border-saffron/30 bg-[rgba(199,80,0,0.04)] p-7 md:p-10">
        <p className="overline-label text-saffron flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> {pick(s.overline_hi, s.overline) || pick("विशेष", "Highlight")}</p>
        <h2 className="mt-3 text-2xl text-ink deva-lh font-deva md:text-3xl">{pick(s.title_hi, s.title) || s.title}</h2>
        {(s.body || s.body_hi) && <div className="mt-4 max-w-3xl text-ink-2"><RichText value={pick(s.body_hi, s.body) || s.body} /></div>}
      </div>
    </section>
  );
}

function RichTextBlock({ s, pick, heading }) {
  return (
    <section className="border-t border-line pt-8" data-testid="festival-block-richtext">
      {heading}
      {s.image && (
        <div className="mt-4 overflow-hidden rounded-lg border border-line bg-parchment">
          <img src={normalizeSrc(s.image)} alt="" className="mx-auto max-h-[420px] w-auto object-contain" />
        </div>
      )}
      {(s.body || s.body_hi) && <div className="mt-4 text-ink-2"><RichText value={pick(s.body_hi, s.body) || s.body} /></div>}
    </section>
  );
}

export default function FestivalPage() {
  const { slug } = useParams();
  const location = useLocation();
  const { pick, lang } = useLang();
  const [f, setF] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setF(null); setNotFound(false);
    fetchFestivalPage(slug).then(setF).catch(() => setNotFound(true));
  }, [slug, location.pathname]);

  if (notFound) return <div className="mx-auto max-w-3xl px-5 py-32 text-center text-ink-3"><T>Festival page not found.</T></div>;
  if (!f) return <OmSpinner />;

  const title = pick(f.title_hi, f.title) || f.title;
  const overline = pick(f.overline_hi, f.overline) || f.overline;
  const intro = pick(f.intro_hi, f.intro) || f.intro;
  const sections = (f.sections || []).filter((s) => !s.hidden);

  const renderSection = (s, i) => {
    const secTitle = pick(s.title_hi, s.title) || s.title;
    const heading = secTitle ? <h2 className={`text-2xl text-ink deva-lh ${lang === "en" ? "font-serif-latin" : "font-deva"}`} data-testid={`festival-section-title-${i}`}>{secTitle}</h2> : null;
    const key = s.id || i;
    const props = { key, s, pick, heading };
    switch (s.type) {
      case "quote": return <QuoteBlock {...props} />;
      case "checklist": return <ChecklistBlock {...props} storeKey={`dh_festp_${f.slug}_${key}`} />;
      case "faq": return <FaqBlock {...props} />;
      case "cards": return <CardsBlock {...props} />;
      case "highlight": return <HighlightBlock {...props} />;
      default: return <RichTextBlock {...props} />;
    }
  };

  return (
    <article className="pb-24" data-testid="festival-page">
      <Seo path={`/festival/${f.slug}`} title={f.title} description={f.seo_description || f.intro} image={f.hero_image} type="article"
        jsonLd={graph(
          articleLd({ title: f.title, description: f.seo_description || f.intro, image: f.hero_image, path: `/festival/${f.slug}` }),
          breadcrumbLd([{ name: "Dharmartha", path: "/" }, { name: "Festivals", path: "/festivals" }, { name: f.title, path: `/festival/${f.slug}` }])
        )} />

      <div className="relative min-h-[44vh] overflow-hidden border-b border-line bg-[var(--parchment)]">
        {f.hero_image && (
          <>
            <img src={normalizeSrc(f.hero_image)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full scale-110 object-cover opacity-45 blur-2xl" />
            <img src={normalizeSrc(f.hero_image)} alt={title} className="absolute inset-0 mx-auto h-full w-auto max-w-full object-contain" />
          </>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 mx-auto max-w-4xl px-5 pb-8 md:px-8">
          {overline && <p className="overline-label text-[var(--gold)]">{overline}</p>}
          <h1 className={`mt-2 text-4xl leading-tight text-white deva-lh md:text-5xl ${lang === "en" ? "font-serif-latin" : "font-deva"}`}>{title}</h1>
          <div className="mt-4"><ShareButton title={`${f.title} · Dharmartha`} text={f.seo_description || f.intro || f.title} label={pick("साझा करें", "Share")} testid={`${f.slug}-share`} /></div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-5 md:px-8">
        {f.countdown_enabled && f.start && (
          <div className="mt-8">
            {(pick(f.countdown_label_hi, f.countdown_label) || "") && <p className="mb-2 overline-label text-saffron">{pick(f.countdown_label_hi, f.countdown_label)}</p>}
            <FestivalCountdown startISO={f.start} endISO={f.end} testid={`${f.slug}-countdown`} />
          </div>
        )}
        {intro && <div className="mt-8 text-lg text-ink-2"><RichText value={intro} /></div>}

        <div className="mt-12 space-y-12">
          {sections.map((s, i) => renderSection(s, i))}
        </div>
      </div>
    </article>
  );
}
